import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { ShieldOff, Shield } from 'lucide-react'
import { useAuth } from '../auth'

/**
 * Studio "arming switch" + helpers for destructive controls.
 *
 * Two-layer gate:
 *   1. The user MUST be `is_superuser`. studio_viewer never sees these
 *      controls regardless of arming state.
 *   2. The user must explicitly ARM the page via the toggle, persisted
 *      to localStorage (per-device, per-user). Default is OFF — the
 *      destructive controls are hidden entirely until armed.
 *
 * The arming switch is a soft UX safety net (not a security boundary —
 * the backend enforces IsPlatformAdmin on the same endpoints). Its job
 * is to prevent accidental clicks on destructive-but-easy-to-miss
 * buttons (delete step, delete dependency, trigger import, etc.).
 *
 * Usage in a page:
 *   const { armed, AdminArmingToggle } = useArmedControls('pipeline')
 *   …
 *   <AdminArmingToggle />
 *   {armed && <button onClick={delete}>Delete step</button>}
 */

const KEY_PREFIX = 'me_studio_armed::'

export function useArmedControls(scope: string) {
  const auth = useAuth()
  const isSuperuser = auth.status === 'authenticated' && auth.user.is_superuser
  const storageKey = KEY_PREFIX + scope

  const [armed, setArmedState] = useState<boolean>(() => {
    try { return localStorage.getItem(storageKey) === '1' } catch { return false }
  })

  const setArmed = (v: boolean) => {
    setArmedState(v)
    try { localStorage.setItem(storageKey, v ? '1' : '0') } catch { /* ignore */ }
  }

  // Auto-disarm when the page unmounts or after long idle, to prevent
  // an old "armed" flag from sticking around silently. Idle timeout: 15min.
  useEffect(() => {
    if (!armed) return
    const t = window.setTimeout(() => setArmed(false), 15 * 60 * 1000)
    return () => window.clearTimeout(t)
  }, [armed])

  function AdminArmingToggle() {
    if (!isSuperuser) return null
    return (
      <button
        onClick={() => setArmed(!armed)}
        className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 inline-flex items-center gap-1"
        title={armed
          ? 'Destructive controls are visible. Click to hide them.'
          : 'Reveal destructive controls (delete / promote / import).'}
        style={armed ? {
          background: 'var(--me-warn, #b45309)', color: 'white',
        } : undefined}
      >
        {armed ? <Shield size={11} /> : <ShieldOff size={11} />}
        {armed ? 'destructive: ARMED' : 'destructive: locked'}
      </button>
    )
  }

  return {
    /** True only if user is superuser AND has armed the page. */
    armed: isSuperuser && armed,
    /** True if the current user is a platform admin. studio_viewer is false. */
    isSuperuser,
    AdminArmingToggle,
    setArmed,
  }
}

/**
 * Quick confirm wrapper. Returns `true` if the user confirms, `false`
 * otherwise. Uses native window.confirm for now — could swap for a
 * styled modal later without changing call sites.
 */
export function confirmDestructive(message: string): boolean {
  return window.confirm(message)
}


// ─── Context: share `armed` across components on a page ────────────────────
//
// When multiple components on the same page need to read the armed state
// (e.g. PipelinePage's header toggle + StepDetail's delete button), wrap
// the page in <ArmedProvider scope="pipeline"> and call useArmed() from
// any descendant. This prevents the per-instance useArmedControls() hook
// from drifting out of sync.

type ArmedCtx = {
  armed: boolean
  isSuperuser: boolean
  AdminArmingToggle: () => ReactNode
  setArmed: (v: boolean) => void
}

const ArmedContext = createContext<ArmedCtx | null>(null)

export function ArmedProvider({ scope, children }: { scope: string; children: ReactNode }) {
  const ctx = useArmedControls(scope)
  return <ArmedContext.Provider value={ctx}>{children}</ArmedContext.Provider>
}

export function useArmed(): ArmedCtx {
  const v = useContext(ArmedContext)
  if (!v) throw new Error('useArmed must be inside <ArmedProvider>.')
  return v
}
